import { Attachment, AttachmentData, AttachmentStatus, AttachmentType } from '../../../@types/types'
import { MultimediaState } from '../../../@types/state-types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { UploadFile } from '../../../@types/multimedia-types'
import { UniqueIdentifier } from '@dnd-kit/core'

export const initialState: MultimediaState = {
  videoInfo: [],
  attachments: [],
  multimediaToDelete: [],
  multimediaToUpdate: [],
}

const prioritizePropertyPhotoAsTitlePicture = (
  attachments: AttachmentData[],
  preferredAttachmentId?: string
): AttachmentData[] => {
  const pictureAttachments = attachments.filter((item) => item.attachmentType === AttachmentType.PICTURE)
  const currentTitlePicture = pictureAttachments[0]

  if (!currentTitlePicture?.attachment?.floorplan) {
    return attachments
  }

  const preferredReplacement = preferredAttachmentId
    ? pictureAttachments.find(
        (item) => item.attachment?.id === preferredAttachmentId && item.attachmentType === AttachmentType.PICTURE
      )
    : undefined
  const replacementPicture = preferredReplacement?.attachment?.floorplan
    ? undefined
    : preferredReplacement || pictureAttachments.find((item) => !item.attachment?.floorplan)

  if (!replacementPicture?.attachment?.id) {
    return attachments
  }

  const currentTitlePictureIndex = attachments.findIndex(
    (item) => item.attachment?.id === currentTitlePicture.attachment?.id
  )
  const replacementIndex = attachments.findIndex((item) => item.attachment?.id === replacementPicture.attachment?.id)

  if (currentTitlePictureIndex === -1 || replacementIndex === -1 || currentTitlePictureIndex === replacementIndex) {
    return attachments
  }

  const reorderedAttachments = [...attachments]
  const [replacementAttachment] = reorderedAttachments.splice(replacementIndex, 1)
  reorderedAttachments.splice(currentTitlePictureIndex, 0, replacementAttachment)

  return reorderedAttachments
}

const multimediaSlice = createSlice({
  name: 'multimediaFeature',
  initialState,
  reducers: {
    startUploadAttachmentRequest(
      state,
      action: PayloadAction<{ attachmentType: AttachmentType; file: UploadFile; openDetailsModalOnAdd?: boolean }>
    ) {
      const { attachmentType, file, openDetailsModalOnAdd = true } = action.payload
      const newAttachment: AttachmentData = {
        attachmentType,
        state: AttachmentStatus.UPLOADING,
        file,
        attachment: undefined,
        openDetailsModalOnAdd,
      }
      if (attachmentType === AttachmentType.DOCUMENT) {
        state.attachments = [...state.attachments, newAttachment]
      } else {
        state.attachments = [
          ...state.attachments.filter((it) => it.attachmentType === AttachmentType.PICTURE),
          newAttachment,
          ...state.attachments.filter((it) => it.attachmentType === AttachmentType.DOCUMENT),
          ...state.attachments.filter((it) => it.attachmentType === AttachmentType.LINK),
          ...state.attachments.filter((it) => it.attachmentType === AttachmentType.VIDEO),
        ]
      }
    },
    progressUploadAttachment(state, action: PayloadAction<{ fileId: string; percent: number }>) {
      const { fileId, percent } = action.payload
      state.attachments = state.attachments.map((it) => {
        if (it.state === AttachmentStatus.UPLOADING && it.file?.id === fileId) {
          return { ...it, file: { ...it.file, percent } }
        } else {
          return it
        }
      })
    },
    updateVideoInfo(state, action: PayloadAction<{ videoInfo: any; offerApiId: string }>) {
      const { videoInfo, offerApiId } = action.payload
      state.videoInfo = state.videoInfo.filter((video) => video.offerApiId !== offerApiId).concat([videoInfo])
    },
    uploadAttachmentSuccess(state, action: PayloadAction<{ fileId: string; attachment: Attachment }>) {
      const { fileId, attachment } = action.payload
      const index = state.attachments.findIndex((it) => it.file?.id === fileId)
      if (index !== -1) {
        state.attachments[index].attachment = attachment
        state.attachments[index].state = AttachmentStatus.PERSISTED
        if (attachment.picture) {
          state.attachments = prioritizePropertyPhotoAsTitlePicture(state.attachments, attachment.id)
        }
      }
    },
    addPersistedAttachmentFromExternalSource(
      state,
      action: PayloadAction<{ attachment: Attachment; fallbackTitle?: string }>
    ) {
      const { attachment, fallbackTitle } = action.payload
      const alreadyExists = state.attachments.some((item) => item.attachment?.id === attachment.id)
      if (alreadyExists) {
        return
      }

      const normalizedAttachment: Attachment = {
        ...attachment,
        title: attachment.title || fallbackTitle || '',
      }
      const attachmentType = normalizedAttachment.picture ? AttachmentType.PICTURE : AttachmentType.DOCUMENT
      const newAttachment: AttachmentData = {
        attachmentType,
        state: AttachmentStatus.PERSISTED,
        attachment: normalizedAttachment,
        file: {
          id: normalizedAttachment.id,
          name: normalizedAttachment.title || '',
          percent: 100,
          size: normalizedAttachment.size,
          type: normalizedAttachment.contentType,
        },
        openDetailsModalOnAdd: false,
      }

      if (attachmentType === AttachmentType.DOCUMENT) {
        state.attachments = [...state.attachments, newAttachment]
      } else {
        state.attachments = [
          ...state.attachments.filter((it) => it.attachmentType === AttachmentType.PICTURE),
          newAttachment,
          ...state.attachments.filter((it) => it.attachmentType === AttachmentType.DOCUMENT),
          ...state.attachments.filter((it) => it.attachmentType === AttachmentType.LINK),
          ...state.attachments.filter((it) => it.attachmentType === AttachmentType.VIDEO),
        ]
        state.attachments = prioritizePropertyPhotoAsTitlePicture(state.attachments, normalizedAttachment.id)
      }
    },
    deleteAttachmentSuccess(state, action: PayloadAction<{ id: string; moveToAttachmentsToDelete: boolean }>) {
      const { id, moveToAttachmentsToDelete } = action.payload
      const attachmentToDelete = state.attachments.find(
        (item) => item.state === AttachmentStatus.PERSISTED && item.attachment?.id === id
      )
      state.attachments = state.attachments.filter(
        (item) => item.state !== AttachmentStatus.PERSISTED || item.attachment?.id !== id
      )
      state.videoInfo = state.videoInfo?.filter((it) => it.offerApiId != attachmentToDelete?.attachment?.offerApiId)
      state.multimediaToUpdate = state.multimediaToUpdate.filter((it) => it.id !== id)
      if (moveToAttachmentsToDelete && attachmentToDelete) {
        state.multimediaToDelete.push(attachmentToDelete.attachment!)
      }
    },
    changeAttachmentOrder(state, action: PayloadAction<{ activeId: UniqueIdentifier; overId: UniqueIdentifier }>) {
      const { activeId, overId } = action.payload
      const activeIndex = state.attachments.findIndex(
        (item) => AttachmentStatus.PERSISTED && item.attachment?.id === activeId
      )
      const overIndex = state.attachments.findIndex(
        (item) => AttachmentStatus.PERSISTED && item.attachment?.id === overId
      )
      if (activeIndex !== -1 && overIndex !== -1) {
        const [movedItem] = state.attachments.splice(activeIndex, 1)
        state.attachments.splice(overIndex, 0, movedItem)
      }
    },
    setPictureOrder(state, action: PayloadAction<string[]>) {
      const orderedIds = action.payload
      const orderedPictures = orderedIds.flatMap((id) => {
        const picture = state.attachments.find(
          (item) =>
            item.state === AttachmentStatus.PERSISTED &&
            item.attachmentType === AttachmentType.PICTURE &&
            item.attachment?.id === id
        )
        return picture ? [picture] : []
      })
      const reorderedIds = new Set(orderedIds)
      let orderedPictureIndex = 0

      state.attachments = state.attachments.map((item) => {
        if (
          item.state !== AttachmentStatus.PERSISTED ||
          item.attachmentType !== AttachmentType.PICTURE ||
          !item.attachment?.id ||
          !reorderedIds.has(item.attachment.id)
        ) {
          return item
        }

        const reorderedPicture = orderedPictures[orderedPictureIndex]
        orderedPictureIndex += 1
        return reorderedPicture ?? item
      })
    },
    changeAttachmentTitle(
      state,
      action: PayloadAction<{ id: string; title: string; moveToAttachmentToUpdate: boolean }>
    ) {
      const { id, title, moveToAttachmentToUpdate } = action.payload
      const index = state.attachments.findIndex((item) => AttachmentStatus.PERSISTED && item.attachment?.id === id)
      if (index !== -1) {
        state.attachments[index].attachment!.title = title
        if (moveToAttachmentToUpdate) {
          const attachmentToUpdateIndex = state.multimediaToUpdate.findIndex(
            (item) => AttachmentStatus.PERSISTED && item?.id === id
          )
          if (attachmentToUpdateIndex !== -1) {
            state.multimediaToUpdate[attachmentToUpdateIndex] = state.attachments[index].attachment!
          } else {
            state.multimediaToUpdate.push(state.attachments[index].attachment!)
          }
        }
      }
    },
    changeFloorplanStatus(state, action: PayloadAction<{ id: string; moveToAttachmentToUpdate: boolean }>) {
      const { id, moveToAttachmentToUpdate } = action.payload
      const index = state.attachments.findIndex((item) => AttachmentStatus.PERSISTED && item.attachment?.id === id)
      if (index !== -1) {
        state.attachments[index].attachment!.floorplan = !state.attachments[index].attachment!.floorplan
        state.attachments = prioritizePropertyPhotoAsTitlePicture(state.attachments)
        if (moveToAttachmentToUpdate) {
          const updatedAttachment = state.attachments.find((item) => item.attachment?.id === id)?.attachment
          if (!updatedAttachment) {
            return
          }
          const attachmentToUpdateIndex = state.multimediaToUpdate.findIndex(
            (item) => AttachmentStatus.PERSISTED && item?.id === id
          )
          if (attachmentToUpdateIndex !== -1) {
            state.multimediaToUpdate[attachmentToUpdateIndex] = updatedAttachment
          } else {
            state.multimediaToUpdate.push(updatedAttachment)
          }
        }
      }
    },
    updatePictureSuccess(
      state,
      action: PayloadAction<{ id: string; attachment: Attachment; moveToAttachmentsToDelete: boolean }>
    ) {
      const { id, attachment, moveToAttachmentsToDelete } = action.payload
      const index = state.attachments.findIndex((item) => AttachmentStatus.PERSISTED && item.attachment?.id === id)
      if (index !== -1) {
        if (moveToAttachmentsToDelete) {
          state.multimediaToDelete.push(state.attachments[index].attachment!)
        }
        state.attachments[index].attachment = attachment
        if (attachment.picture) {
          state.attachments = prioritizePropertyPhotoAsTitlePicture(state.attachments, attachment.id)
        }
      }
    },
    getAttachmentsInitialCompletionSuccess(state, action: PayloadAction<{ initAttachments: AttachmentData[] }>) {
      state.attachments = action.payload.initAttachments
    },
    clearMultimediaOperations(state) {
      state.multimediaToUpdate = []
      state.multimediaToDelete = []
    },
    setAttachmentsOnOfferPublish(
      state,
      action: PayloadAction<{ attachmentsToAdd: AttachmentData[]; attachmentsToDelete: Attachment[] }>
    ) {
      const { attachmentsToAdd, attachmentsToDelete } = action.payload
      const notUploadedAttachments = state.attachments.filter(
        (it) => !attachmentsToDelete.find((attachmentToDelete) => it.attachment?.id === attachmentToDelete.id)
      )
      const picturesToAdd = attachmentsToAdd.filter((it) => it.attachment?.picture)
      const documentToAdd = attachmentsToAdd.filter((it) => it.attachment?.document)
      const linkToAdd = attachmentsToAdd.filter((it) => it.attachment?.link)
      const videoToAdd = attachmentsToAdd.filter((it) => it.attachment?.video)
      const notUploadedPictures = notUploadedAttachments.filter((it) => it.attachmentType === AttachmentType.PICTURE)
      const notUploadedDocuments = notUploadedAttachments.filter((it) => it.attachmentType === AttachmentType.DOCUMENT)
      const notUploadedLinks = notUploadedAttachments.filter((it) => it.attachmentType === AttachmentType.LINK)
      const notUploadedVideos = notUploadedAttachments.filter((it) => it.attachmentType === AttachmentType.VIDEO)
      state.attachments = [
        ...picturesToAdd,
        ...notUploadedPictures,
        ...documentToAdd,
        ...notUploadedDocuments,
        ...linkToAdd,
        ...notUploadedLinks,
        ...videoToAdd,
        ...notUploadedVideos,
      ]
    },
    uploadAttachmentLink(state, action: PayloadAction<{ attachment: Attachment }>) {
      const newAttachment: AttachmentData = {
        attachmentType: AttachmentType.LINK,
        state: AttachmentStatus.PERSISTED,
        attachment: action.payload.attachment,
        openDetailsModalOnAdd: true,
      }
      state.attachments.push(newAttachment)
    },
  },
})

export const {
  startUploadAttachmentRequest,
  progressUploadAttachment,
  updateVideoInfo,
  uploadAttachmentSuccess,
  addPersistedAttachmentFromExternalSource,
  deleteAttachmentSuccess,
  changeAttachmentOrder,
  setPictureOrder,
  changeAttachmentTitle,
  changeFloorplanStatus,
  updatePictureSuccess,
  getAttachmentsInitialCompletionSuccess,
  clearMultimediaOperations,
  setAttachmentsOnOfferPublish,
  uploadAttachmentLink,
} = multimediaSlice.actions

export default multimediaSlice.reducer
